import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Tooltip, type TooltipTriggerProps } from "@sumprio/ui";
import { useListTableScrollBoundaryRef } from "./ListTableScrollBoundaryContext";

const GAP_PX = 6;
const VIEWPORT_PAD_PX = 8;
const HIDE_DELAY_MS = 120;
const BOUNDARY_TOOLTIP_Z = 80;

type Placement = NonNullable<TooltipTriggerProps["placement"]>;

type AnchorBox = { x: number; y: number; w: number; h: number };

function collectScrollTargets(el: HTMLElement | null): Array<HTMLElement | Window> {
  const list: Array<HTMLElement | Window> = [window];
  let node: HTMLElement | null = el?.parentElement ?? null;
  while (node) {
    const { overflowX, overflowY } = getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(overflowX) || /(auto|scroll|overlay)/.test(overflowY)) {
      list.push(node);
    }
    node = node.parentElement;
  }
  return list;
}

function computeTooltipTopLeft(
  placement: Placement,
  anchor: AnchorBox,
  tipW: number,
  tipH: number
): { left: number; top: number } {
  const { x, y, w, h } = anchor;
  let left = 0;
  let top = 0;

  switch (placement) {
    case "top":
      left = x + w / 2 - tipW / 2;
      top = y - tipH - GAP_PX;
      break;
    case "top-start":
      left = x;
      top = y - tipH - GAP_PX;
      break;
    case "top-end":
      left = x + w - tipW;
      top = y - tipH - GAP_PX;
      break;
    case "bottom":
      left = x + w / 2 - tipW / 2;
      top = y + h + GAP_PX;
      break;
    case "bottom-start":
      left = x;
      top = y + h + GAP_PX;
      break;
    case "bottom-end":
      left = x + w - tipW;
      top = y + h + GAP_PX;
      break;
    case "left":
      left = x - tipW - GAP_PX;
      top = y + h / 2 - tipH / 2;
      break;
    case "left-start":
      left = x - tipW - GAP_PX;
      top = y;
      break;
    case "left-end":
      left = x - tipW - GAP_PX;
      top = y + h - tipH;
      break;
    case "right":
      left = x + w + GAP_PX;
      top = y + h / 2 - tipH / 2;
      break;
    case "right-start":
      left = x + w + GAP_PX;
      top = y;
      break;
    case "right-end":
      left = x + w + GAP_PX;
      top = y + h - tipH;
      break;
    default:
      left = x + w / 2 - tipW / 2;
      top = y - tipH - GAP_PX;
  }

  return { left, top };
}

function clampBox(
  left: number,
  top: number,
  tipW: number,
  tipH: number,
  minX: number,
  minY: number,
  maxX: number,
  maxY: number
): { left: number; top: number } {
  const sx = Math.min(minX, maxX);
  const bx = Math.max(minX, maxX);
  const sy = Math.min(minY, maxY);
  const by = Math.max(minY, maxY);
  return {
    left: Math.min(Math.max(sx, left), bx),
    top: Math.min(Math.max(sy, top), by),
  };
}

function findViewportClampRoot(anchorEl: HTMLElement): HTMLElement | null {
  const card = anchorEl.closest(".card");
  if (card instanceof HTMLElement) return card;
  const listCard = anchorEl.closest(".list-table-card");
  if (listCard instanceof HTMLElement) return listCard;
  return null;
}

function positionViewport(
  placement: Placement,
  anchorEl: HTMLElement,
  tipW: number,
  tipH: number
): { left: number; top: number } {
  const ar = anchorEl.getBoundingClientRect();
  const anchorBox: AnchorBox = { x: ar.left, y: ar.top, w: ar.width, h: ar.height };
  const raw = computeTooltipTopLeft(placement, anchorBox, tipW, tipH);

  const clampRoot = findViewportClampRoot(anchorEl);
  if (clampRoot) {
    const b = clampRoot.getBoundingClientRect();
    return clampBox(
      raw.left,
      raw.top,
      tipW,
      tipH,
      b.left + VIEWPORT_PAD_PX,
      b.top + VIEWPORT_PAD_PX,
      b.right - tipW - VIEWPORT_PAD_PX,
      b.bottom - tipH - VIEWPORT_PAD_PX
    );
  }

  return clampBox(
    raw.left,
    raw.top,
    tipW,
    tipH,
    VIEWPORT_PAD_PX,
    VIEWPORT_PAD_PX,
    Math.max(VIEWPORT_PAD_PX, window.innerWidth - tipW - VIEWPORT_PAD_PX),
    Math.max(VIEWPORT_PAD_PX, window.innerHeight - tipH - VIEWPORT_PAD_PX)
  );
}

function positionInsideBoundary(
  placement: Placement,
  anchorEl: HTMLElement,
  boundaryEl: HTMLElement,
  tipW: number,
  tipH: number
): { left: number; top: number } {
  const br = boundaryEl.getBoundingClientRect();
  const ar = anchorEl.getBoundingClientRect();
  const ax = ar.left - br.left + boundaryEl.scrollLeft;
  const ay = ar.top - br.top + boundaryEl.scrollTop;
  const anchorBox: AnchorBox = { x: ax, y: ay, w: ar.width, h: ar.height };
  const raw = computeTooltipTopLeft(placement, anchorBox, tipW, tipH);

  const sl = boundaryEl.scrollLeft;
  const st = boundaryEl.scrollTop;
  const cw = boundaryEl.clientWidth;
  const ch = boundaryEl.clientHeight;

  const horizMin = sl + VIEWPORT_PAD_PX;
  const horizMax = sl + cw - tipW - VIEWPORT_PAD_PX;

  const vertMin = st + VIEWPORT_PAD_PX;
  const vertMax = st + ch - tipH - VIEWPORT_PAD_PX;

  return clampBox(raw.left, raw.top, tipW, tipH, horizMin, vertMin, horizMax, vertMax);
}

export function SafeTooltipTrigger({
  tooltip,
  supportingText,
  children,
  disabled = false,
  placement = "top",
  theme = "light",
  showArrow = true,
  className,
  style,
  width,
  minWidth,
  maxWidth,
}: TooltipTriggerProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const tipShellRef = useRef<HTMLDivElement>(null);
  const hideTimerRef = useRef<number | null>(null);
  const [coords, setCoords] = useState<{ left: number; top: number } | null>(null);

  const scrollBoundaryRef = useListTableScrollBoundaryRef();

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current !== null) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearHideTimer(), [clearHideTimer]);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const shell = tipShellRef.current;
    if (!anchor || !shell || !open) return;
    const tw = shell.offsetWidth;
    const th = shell.offsetHeight;
    if (tw < 1 || th < 1) return;

    const boundary = scrollBoundaryRef?.current ?? null;
    const inside =
      boundary !== null && boundary !== undefined && boundary.contains(anchor) ? boundary : null;

    if (inside) {
      setCoords(positionInsideBoundary(placement as Placement, anchor, inside, tw, th));
    } else {
      setCoords(positionViewport(placement as Placement, anchor, tw, th));
    }
  }, [open, placement, scrollBoundaryRef]);

  useLayoutEffect(() => {
    if (!open || disabled) {
      setCoords(null);
      return;
    }

    let raf = 0;
    const run = (): void => {
      const shell = tipShellRef.current;
      const anchor = anchorRef.current;
      if (!shell || !anchor) return;
      if (shell.offsetWidth < 1 || shell.offsetHeight < 1) {
        raf = requestAnimationFrame(run);
        return;
      }
      updatePosition();
    };

    raf = requestAnimationFrame(run);
    const targets = collectScrollTargets(anchorRef.current);
    targets.forEach((t) => {
      t.addEventListener("scroll", updatePosition, true);
    });
    window.addEventListener("resize", updatePosition);

    return () => {
      cancelAnimationFrame(raf);
      targets.forEach((t) => {
        t.removeEventListener("scroll", updatePosition, true);
      });
      window.removeEventListener("resize", updatePosition);
    };
  }, [
    open,
    disabled,
    placement,
    tooltip,
    supportingText,
    width,
    minWidth,
    maxWidth,
    updatePosition,
  ]);

  const handleEnter = (): void => {
    clearHideTimer();
    if (!disabled) setOpen(true);
  };
  const handleLeave = (): void => {
    clearHideTimer();
    hideTimerRef.current = window.setTimeout(() => {
      hideTimerRef.current = null;
      setOpen(false);
      setCoords(null);
    }, HIDE_DELAY_MS);
  };

  const dismissTooltipNow = useCallback((): void => {
    clearHideTimer();
    setOpen(false);
    setCoords(null);
  }, [clearHideTimer]);

  const anchor = anchorRef.current;
  const boundary = scrollBoundaryRef?.current ?? null;
  const portalHost: HTMLElement | null =
    open && anchor && boundary && boundary.contains(anchor) ? boundary : document.body;

  const useFixedLayer = portalHost === document.body;

  const portal =
    open && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={tipShellRef}
            style={{
              position: useFixedLayer ? "fixed" : "absolute",
              left: coords?.left ?? 0,
              top: coords?.top ?? 0,
              zIndex: useFixedLayer ? 100300 : BOUNDARY_TOOLTIP_Z,
              pointerEvents: "none",
              visibility: coords ? "visible" : "hidden",
            }}
          >
            <Tooltip
              placement={placement}
              theme={theme}
              showArrow={showArrow}
              supportingText={supportingText}
              width={width}
              minWidth={minWidth}
              maxWidth={maxWidth}
            >
              {tooltip}
            </Tooltip>
          </div>,
          portalHost
        )
      : null;

  return (
    <>
      <div
        ref={anchorRef}
        className={className}
        style={{
          position: "relative",
          display: "inline-flex",
          ...style,
        }}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onPointerDownCapture={dismissTooltipNow}
        onFocusCapture={dismissTooltipNow}
      >
        {children}
      </div>
      {portal}
    </>
  );
}
